import React, { useCallback } from 'react';
import { Thumbnail, ThumbnailSmall } from '../item/Thumbnail';
import Button from 'react-bootstrap/Button';
import { useNavigate } from 'react-router-dom';
import { urlForItem } from '../../fleet-shared/Urls';
import { TaskSummary } from './TaskSummary';
import { itemTypeIcon, itemTypeLabel } from '../../fleet-shared/ItemTypes.mjs';
import { isLogOnly } from '../../fleet-shared/ItemTracking.mjs';

// One row of the Items list. Renders as a <tr> so the parent <Table> in
// Items.jsx handles striping/hover/spacing. The Details control is a compact
// chevron button pinned to the right of the row at every width.
// `soonDays` / `soonCounter` are the item's due-soon window, handed through to
// TaskSummary so the row's Overdue / Due soon counts follow the item's own
// setting; absent means the default.
export const ItemRow = ({ identityId, itemId, name, itemType = null, tracking = null, soonDays = null, soonCounter = null }) => {
  const navigate = useNavigate();

  const onClickRow = useCallback(() => {
    navigate(urlForItem(itemId));
  }, [itemId, navigate]);

  const onClickDetails = useCallback((e) => {
    e.stopPropagation();
    onClickRow();
  }, [onClickRow]);

  return (
    <tr className="item-row-main" onClick={onClickRow} role="button" style={{ cursor: 'pointer' }}>
      <td className="align-middle item-name-cell">
        <div className="item-name-stack">
          {/*
            aria-hidden with a title: the row's accessible name stays the item
            name alone (several e2e specs and Items.test.jsx find rows by it),
            while a sighted reader can still hover to learn what the glyph
            means. An item with no type falls through to the neutral fa-cube --
            which is what every row rendered before this feature existed.
          */}
          <i
            className={`${itemTypeIcon(itemType)} item-type-icon`}
            title={itemTypeLabel(itemType)}
            aria-hidden="true"
          />
          <span className="entered-data">{name}</span>
          {/* Muted, not a color badge: it explains why this row has no task
              summary, it doesn't demand attention. Kept outside the row's
              accessible name for the same reason as the type icon. */}
          {isLogOnly({ tracking }) && (
            <span className="badge text-bg-light text-body-secondary fw-normal" aria-hidden="true">
              Trips &amp; logs
            </span>
          )}
          <Thumbnail identityId={identityId} itemId={itemId} size={ThumbnailSmall} />
        </div>
      </td>
      <td className="align-middle item-task-summary-cell">
        <TaskSummary itemId={itemId} soonDays={soonDays} soonCounter={soonCounter} />
      </td>
      <td
        className="align-middle text-end item-details-cell"
        style={{ width: 56 }}
      >
        <Button
          variant="outline-primary"
          size="sm"
          onClick={onClickDetails}
          aria-label="Details"
        >
          <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
        </Button>
      </td>
    </tr>
  );
};
